"use client";

import { NFONE_MAX_OPEN_BRAND_APPLICATIONS } from "@newform/constants";
import { AtSign, Ban, Check, Clock, UserRound } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useRef } from "react";
import { QUIET_HOVER } from "@/components/creatorPortal/one/chrome";
import { rememberSignInEmail } from "@/components/creatorPortal/signInEmailHint";
import { Button } from "@/components/ui/design-system/button";
import { SURFACE } from "@/components/ui/design-system/surface-styles";
import {
  Body,
  Caption,
  Display,
  Rail,
  Strong,
} from "@/components/ui/design-system/text";
import { sharedAuthClient } from "@/lib/sharedAuthClient";
import { cn } from "@/lib/utils";
import type { HandleProblem, SubmitOutcome } from "./applyForm";

/**
 * What the application becomes once it is sent.
 *
 * The portal's `SubmitDone` pattern: the answer replaces the form rather than
 * appearing under it, a tick in a tinted disc rather than a large glyph, and
 * no pane around the headline, because the page is the card. It arrives
 * through the flow's crossfade.
 *
 * WHAT A SENTENCE COULD NOT SAY. "Application received" answered whether it
 * worked and nothing about what happens now, which is the question someone
 * has the moment it did. So the received state names the inbox the agreement
 * went to, lays out the three things that happen after, ending on the bonus
 * they applied for, and hands them straight to the first of them: choosing
 * brands, which happens signed in at /one. There is no NewForm-wide review
 * to wait for; each brand decides on its own.
 *
 * It settles in rather than cutting: the mark first, then the words, then
 * what's next, a beat apart. This screen is seen once per person, which is
 * the one place an entrance can afford to be noticed. Reduced motion keeps
 * the fades and drops the travel.
 */

const ENTER =
  "transition-[opacity,translate,scale] duration-nf-ds-5 ease-nf-ds-out starting:opacity-0 motion-safe:starting:translate-y-nf-ds-2";

const TONE: Record<SubmitOutcome, { icon: ReactNode; disc: string }> = {
  received: {
    icon: <Check className="size-nf-ds-6" strokeWidth={2.4} />,
    disc: "bg-nf-ds-tone-ok-bg text-nf-ds-tone-ok-fg",
  },
  auto_rejected: {
    icon: <AtSign className="size-nf-ds-6" strokeWidth={2} />,
    disc: "bg-nf-ds-tone-warn-bg text-nf-ds-tone-warn-fg",
  },
  already_applied: {
    icon: <Clock className="size-nf-ds-6" strokeWidth={2} />,
    disc: "bg-nf-ds-tone-info-bg text-nf-ds-tone-info-fg",
  },
  already_a_creator: {
    icon: <UserRound className="size-nf-ds-6" strokeWidth={2} />,
    disc: "bg-nf-ds-tone-brand-bg text-nf-ds-tone-brand-fg",
  },
  // Neutral, not the error red: this is a decision being stated, not a
  // failure being reported, and there is nothing on the page to fix.
  blocked: {
    icon: <Ban className="size-nf-ds-6" strokeWidth={2} />,
    disc: "bg-nf-ds-tone-neutral-bg text-nf-ds-tone-neutral-fg",
  },
};

const NEXT = ["brands", "review", "portal"] as const;

export function ApplyOutcome({
  outcome,
  email,
  supportEmail,
  handleProblems,
  onFixHandles,
}: {
  outcome: SubmitOutcome;
  /** The address they applied with, where every next step will arrive. */
  email: string;
  supportEmail: string;
  /** For `auto_rejected`: each handle that led nowhere, and why. */
  handleProblems: HandleProblem[];
  /** Returns to the handles step with the rest of the answers kept. */
  onFixHandles: () => void;
}) {
  const t = useTranslations("apply.outcome");
  const headingRef = useRef<HTMLElement>(null);
  // Already signed in as the address they applied with: /one opens straight
  // onto the brand list. Anyone else signs in there first, and the address
  // is carried over so the field starts filled in.
  const session = sharedAuthClient.useSession();
  const signedInAsApplicant =
    session.data?.user.email.trim().toLowerCase() ===
    email.trim().toLowerCase();
  const rememberEmail = () => {
    if (!signedInAsApplicant) rememberSignInEmail(email);
  };

  // The form that had focus is gone. Put the reader on the answer, and at
  // the top of it, rather than leaving focus on nothing mid-page.
  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, []);

  const emphasis = (chunks: ReactNode) => (
    <span className="text-nf-ds-fg">{chunks}</span>
  );

  return (
    <div className="flex flex-col items-center gap-nf-ds-8 text-center">
      <header
        ref={headingRef}
        tabIndex={-1}
        className="flex flex-col items-center gap-nf-ds-4 outline-hidden"
      >
        <span
          aria-hidden="true"
          className={cn(
            "grid size-nf-ds-12 place-items-center rounded-nf-ds-pill",
            TONE[outcome].disc,
            ENTER,
            "motion-safe:starting:scale-90",
          )}
        >
          {TONE[outcome].icon}
        </span>
        <div
          className={cn(
            "flex flex-col items-center gap-nf-ds-2 delay-75",
            ENTER,
          )}
        >
          <Display as="h1" className="text-balance">
            {outcome === "received"
              ? t("received.title")
              : outcome === "auto_rejected"
                ? t("autoRejected.title")
                : outcome === "already_applied"
                  ? t("alreadyApplied.title")
                  : outcome === "blocked"
                    ? t("blocked.title")
                    : t("alreadyCreator.title")}
          </Display>
          <Body muted className="text-pretty">
            {outcome === "received"
              ? t.rich("received.lede", { address: email, emphasis })
              : outcome === "auto_rejected"
                ? t.rich("autoRejected.body", { address: email, emphasis })
                : outcome === "already_applied"
                  ? t.rich("alreadyApplied.body", {
                      address: supportEmail,
                      emphasis,
                    })
                  : outcome === "blocked"
                    ? t("blocked.body")
                    : t.rich("alreadyCreator.body", {
                        address: email,
                        emphasis,
                      })}
          </Body>
        </div>
      </header>

      {outcome === "auto_rejected" && handleProblems.length > 0 ? (
        // The same per-handle reasons the email carries, so the fix is on
        // screen without opening the inbox.
        <ul
          className={cn(
            SURFACE,
            "flex w-full flex-col gap-nf-ds-2 p-nf-ds-6 text-left delay-150",
            ENTER,
          )}
        >
          {handleProblems.map((problem) => (
            <li key={`${problem.platform}:${problem.value}`}>
              <Body className="text-pretty">
                {t(`autoRejected.problems.${problem.problem}`, {
                  platform:
                    problem.platform === "tiktok" ? "TikTok" : "Instagram",
                  value: problem.value,
                })}
              </Body>
            </li>
          ))}
        </ul>
      ) : null}

      {outcome === "received" ? (
        <section
          aria-labelledby="apply-next"
          className={cn(SURFACE, "w-full p-nf-ds-6 text-left delay-150", ENTER)}
        >
          <Rail as="h2" id="apply-next" muted>
            {t("received.nextTitle")}
          </Rail>
          <ol className="mt-nf-ds-4 flex flex-col gap-nf-ds-5">
            {NEXT.map((step, index) => (
              <li key={step} className="flex gap-nf-ds-3">
                <Caption muted className="pt-nf-ds-1">
                  {`0${index + 1}`}
                </Caption>
                <div className="flex flex-col gap-nf-ds-1">
                  <Strong>{t(`received.next.${step}.title`)}</Strong>
                  <Body muted className="text-pretty">
                    {t(`received.next.${step}.body`, {
                      max: NFONE_MAX_OPEN_BRAND_APPLICATIONS,
                    })}
                  </Body>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <div className={cn("flex w-full flex-col gap-nf-ds-3 delay-300", ENTER)}>
        {outcome === "auto_rejected" ? (
          <Button variant="ink" className="w-full" onClick={onFixHandles}>
            {t("autoRejected.fix")}
          </Button>
        ) : null}
        {outcome === "received" || outcome === "already_applied" ? (
          <Button asChild variant="ink" className="w-full">
            <Link href="/one" onClick={rememberEmail}>
              {signedInAsApplicant
                ? t("received.choose")
                : t("received.signInToChoose")}
            </Link>
          </Button>
        ) : null}
        {outcome === "already_a_creator" ? (
          <Button asChild variant="ink" className="w-full">
            <Link href="/join/link" onClick={rememberEmail}>
              {t("alreadyCreator.signIn")}
            </Link>
          </Button>
        ) : null}
        <Button asChild variant="ghost" className={cn("w-full", QUIET_HOVER)}>
          <Link href="/join">{t("back")}</Link>
        </Button>
      </div>
    </div>
  );
}
